import { PropertyType } from '~/client/graph/types.generated'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  getResourceDefinition,
  getResourceFieldDefinition,
  isResourcePropertyBackedField,
} from '~/client/src/shared/resources/definitions'
import {
  IResourceRecord,
  IResourceStatusBadge,
  RESOURCE_NAME_KEY,
} from '~/client/src/shared/resources/definitions/types'
import { renderFieldValueIcon } from '~/client/src/shared/resources/icons/fieldValueIcons'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import {
  isEmptyResourceValue,
  RESOURCE_EMPTY_VALUE,
} from '~/client/src/shared/resources/presentation/formatResourceField'
import { resolveResourceValue } from '~/client/src/shared/resources/resolveResourceValue'
import { getHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'

import { getResourceAttachments } from '../ResourcePicker/getResourceAttachments'
import {
  IResourceCardMapperInput,
  IResourceCardMetaItem,
  IResourceCardModel,
  ResourceCardMetaKind,
} from './types'

const resolveStatusBadge = ({
  record,
  resourceTypeId,
  availablePropertyNames,
}: {
  record: IResourceRecord
  resourceTypeId?: string
  availablePropertyNames: ReadonlySet<string>
}): IResourceStatusBadge | undefined => {
  const definition = getResourceDefinition(resourceTypeId)
  if (!definition) return undefined

  for (const [fieldName, fieldDefinition] of Object.entries(
    definition.fields,
  )) {
    const card = fieldDefinition.card
    if (!card?.isStatus || !card.resolveStatusBadge) continue
    if (
      !availablePropertyNames.has(fieldName) &&
      isResourcePropertyBackedField(resourceTypeId, fieldName)
    ) {
      continue
    }

    const value = resolveResourceValue({
      record,
      resourceTypeId,
      key: fieldName,
    })
    const badge = card.resolveStatusBadge(value)
    if (badge) return badge
  }

  return undefined
}

const METRIC_PROPERTY_TYPES = new Set(['number', 'rollup', 'computed_field'])

const toDisplayValue = (
  propertyName: string,
  value: any,
  getDisplayValue: (propertyName: string, value: any) => string,
): string => {
  if (isEmptyResourceValue(value)) {
    return RESOURCE_EMPTY_VALUE
  }

  const displayValue = getDisplayValue(propertyName, value)
  if (!displayValue) {
    return RESOURCE_EMPTY_VALUE
  }

  return displayValue
}

const getDefaultMetaKind = (
  property: IResourceCardMapperInput['properties'][number] | undefined,
): ResourceCardMetaKind => {
  if (property?.configuration?.unit) {
    return 'metric'
  }

  if (property && METRIC_PROPERTY_TYPES.has(property.propertyType)) {
    return 'metric'
  }

  return 'text'
}

export const buildResourceCardModel = ({
  record,
  resourceTypeId,
  cardConfiguration,
  properties,
  titleProperty = RESOURCE_NAME_KEY,
  getDisplayValue,
  resolveMetaItem,
  resolveSubtitle,
}: IResourceCardMapperInput): IResourceCardModel => {
  const availablePropertyNames = new Set(
    properties.map(property => property.name),
  )
  const isFieldAvailable = (propertyName: string): boolean =>
    availablePropertyNames.has(propertyName) ||
    !isResourcePropertyBackedField(resourceTypeId, propertyName)
  const titleValue =
    titleProperty === RESOURCE_NAME_KEY || !isFieldAvailable(titleProperty)
      ? record.name
      : (resolveResourceValue({
          record,
          resourceTypeId,
          key: titleProperty,
        }) ?? record.name)
  const title =
    titleValue === null || titleValue === undefined || titleValue === ''
      ? record.name
      : String(titleValue)

  const subtitleProperty = cardConfiguration.subtitleProperty
  const subtitleText =
    subtitleProperty && isFieldAvailable(subtitleProperty)
      ? toDisplayValue(
          subtitleProperty,
          resolveResourceValue({
            record,
            resourceTypeId,
            key: subtitleProperty,
          }),
          getDisplayValue,
        )
      : null
  const resolvedSubtitle =
    subtitleText && subtitleText !== RESOURCE_EMPTY_VALUE ? subtitleText : null
  const overriddenSubtitle = resolveSubtitle?.({
    record,
    subtitle: resolvedSubtitle,
  })
  const subtitle =
    overriddenSubtitle === undefined ? resolvedSubtitle : overriddenSubtitle

  const displayProperties = cardConfiguration.displayProperties || []

  const status = resolveStatusBadge({
    record,
    resourceTypeId,
    availablePropertyNames,
  })

  const metaItems: IResourceCardMetaItem[] = displayProperties
    .map(propertyName => {
      if (!isFieldAvailable(propertyName)) {
        return null
      }

      const property = properties.find(item => item.name === propertyName)
      const value = resolveResourceValue({
        record,
        resourceTypeId,
        key: propertyName,
      })
      const displayText = toDisplayValue(propertyName, value, getDisplayValue)
      const fieldDefinition = getResourceFieldDefinition(
        resourceTypeId,
        propertyName,
      )
      const card = fieldDefinition?.card
      const resolverResult = resolveMetaItem?.({
        propertyName,
        property,
        value,
        displayText,
        record,
      })

      if (
        resolverResult?.hidden ||
        card?.visible === false ||
        card?.isStatus ||
        displayText === RESOURCE_EMPTY_VALUE
      ) {
        return null
      }

      const iconKey = card?.iconKey
      const baseIcon = iconKey ? renderResourceSemanticIcon(iconKey) : undefined
      // A field naming a person, company or location shows that entity's own image.
      const valueIcon = card?.iconKind
        ? renderFieldValueIcon(card.iconKind, value, baseIcon)
        : undefined

      // A field naming a linked equipment shows that equipment's own glyph.
      const linkedEquipmentIcon = card?.resolveLinkedEquipment
        ? renderResourceRecordIcon(
            ResourceTypeId.OFFLOADING_EQUIPMENT,
            card.resolveLinkedEquipment(record),
            baseIcon,
            'tw:block tw:size-full',
          )
        : undefined

      const kind =
        resolverResult?.kind || card?.kind || getDefaultMetaKind(property)
      const icon =
        resolverResult?.icon || valueIcon || linkedEquipmentIcon || baseIcon

      let text = resolverResult?.text || displayText
      let breadcrumb: string | undefined
      if (
        !resolverResult?.text &&
        property?.propertyType === PropertyType.Hierarchy &&
        property.configuration?.options
      ) {
        const single = Array.isArray(value)
          ? value.length === 1
            ? value[0]
            : undefined
          : value
        if (single != null) {
          const parts = getHierarchyAccessors(
            property.configuration.options,
          ).getDisplayParts(String(single))
          text = parts.leaf
          breadcrumb = parts.ancestorPath || undefined
        }
      }

      return {
        key: propertyName,
        kind,
        text,
        breadcrumb,
        icon,
        color: resolverResult?.color,
        iconKind: card?.iconKind,
        entityId:
          card?.iconKind && value != null && value !== ''
            ? String(value)
            : undefined,
        onClick: kind === 'link' ? resolverResult?.onClick : undefined,
      }
    })
    .filter(Boolean) as IResourceCardMetaItem[]

  const attachments = getResourceAttachments(record, properties)

  return {
    title,
    subtitle,
    status,
    metaItems,
    attachments: attachments.length > 0 ? attachments : undefined,
  }
}
